/** Mirrors projects ProjectStatus (ADR-071 native lifecycle). */
export const PROJECT_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number]['value'];

export const PROJECT_VISIBILITIES = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
] as const;

export type ProjectVisibility = (typeof PROJECT_VISIBILITIES)[number]['value'];

export const ESTIMATE_TYPES = [
  { value: 'points', label: 'Story points' },
  { value: 'hours', label: 'Hours' },
] as const;

export type EstimateType = (typeof ESTIMATE_TYPES)[number]['value'];

/** Rotating palette for auto-coloring inline-created work-item labels. */
export const LABEL_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#84cc16', // lime
  '#10b981', // emerald
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#a855f7', // purple
  '#ec4899', // pink
] as const;

export const PRIORITIES = [
  { value: 'none', label: 'None' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

export type Priority = (typeof PRIORITIES)[number]['value'];

/** Work-item state groups — the rollup axis (Overview progress + analytics). */
export const STATE_GROUPS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'unstarted', label: 'Unstarted' },
  { value: 'started', label: 'Started' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type StateGroup = (typeof STATE_GROUPS)[number]['value'];

/** State groups you can log time against — work must be underway or done. Excludes
 *  backlog / unstarted (not started yet) and cancelled (won't be done). Used to
 *  scope the worklog work-item picker. */
export const LOGGABLE_STATE_GROUPS = ['started', 'completed'] as const;

export const WORK_ITEM_RELATION_KINDS = [
  { value: 'blocks', label: 'Blocks' },
  { value: 'blocked_by', label: 'Blocked by' },
  { value: 'relates_to', label: 'Relates to' },
  { value: 'duplicate', label: 'Duplicate' },
] as const;

export type WorkItemRelationKind = (typeof WORK_ITEM_RELATION_KINDS)[number]['value'];

export const MODULE_STATUSES = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type ModuleStatus = (typeof MODULE_STATUSES)[number]['value'];

export const VIEW_LAYOUTS = [
  { value: 'list', label: 'List' },
  { value: 'board', label: 'Board' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'table', label: 'Table' },
  { value: 'gantt', label: 'Gantt' },
] as const;

export type ViewLayout = (typeof VIEW_LAYOUTS)[number]['value'];

export const PROJECT_MEMBER_ROLES = [
  { value: 'lead', label: 'Lead' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
] as const;

export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number]['value'];

export const MILESTONE_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
] as const;

export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number]['value'];

export const projectStatusLabel = (s: string): string =>
  PROJECT_STATUSES.find((x) => x.value === s)?.label ?? s;

export function projectStatusVariant(
  s: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'active') return 'default';
  if (s === 'paused' || s === 'planned' || s === 'draft') return 'secondary';
  if (s === 'cancelled') return 'destructive';
  return 'outline'; // completed
}

export const priorityLabel = (s: string): string =>
  PRIORITIES.find((x) => x.value === s)?.label ?? s;

export const milestoneStatusLabel = (s: string): string =>
  MILESTONE_STATUSES.find((x) => x.value === s)?.label ?? s;

/** Worklog note cap — the `TimeEntry.description` column (BE-ADR-109). The API
 *  refuses longer notes rather than cutting them. */
export const WORKLOG_NOTE_MAX_CHARS = 255;
